import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { AdminTabs } from "../components/admin/AdminTabs";
import { PaymentRequestForm } from "../components/admin/PaymentRequestForm";
import { ReceiptActions } from "../components/admin/ReceiptActions";
import { StaffTestBooking } from "../components/admin/StaffTestBooking";
import { LinkStatusBoard } from "../components/admin/LinkStatusBoard";
import { ResendBanner } from "../components/admin/ResendBanner";
import { api, formatApiErrorDetail } from "../lib/api";

const BADGE = { paid: "bg-emerald-100 text-emerald-800", unpaid: "bg-amber-100 text-amber-800", expired: "bg-stone-200 text-stone-600", cancelled: "bg-stone-200 text-stone-600" };
const when = (iso) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });

const LinkPanel = ({ req, onClose }) => (
  <div data-testid="payreq-link-panel" className="bg-cream-100 border border-bronze-light/50 rounded-md p-6 flex flex-wrap gap-6 items-center">
    <QRCodeSVG value={req.url} size={168} data-testid="payreq-qr" />
    <div className="flex-1 min-w-[240px] space-y-3">
      <div className="font-serif text-2xl">{req.patient_name} · £{(req.total / 100).toFixed(2)}</div>
      <p className="text-sm text-ink-muted">The patient can scan this code with their phone camera, or you can open the payment page on this computer.</p>
      <div className="flex flex-wrap gap-2">
        <a href={req.url} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="payreq-open">Pay on this computer</a>
        <button type="button" className="btn-secondary" onClick={() => navigator.clipboard.writeText(req.url).then(() => toast.success("Link copied."))} data-testid="payreq-copy">Copy link</button>
        <button type="button" className="text-sm underline" onClick={onClose} data-testid="payreq-close">Close</button>
      </div>
    </div>
  </div>
);

const Row = ({ r, onShow, onChanged }) => {
  const act = (path, msg) => api.post(`/admin/payment-requests/${r.token}/${path}`).then(() => { toast.success(msg); onChanged(); })
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  return (
    <tr className="border-t border-stone-200/70 text-sm" data-testid={`payreq-row-${r.token}`}>
      <td className="py-3 pr-3"><span className={`px-2 py-0.5 rounded-full text-xs ${BADGE[r.status]}`} data-testid={`payreq-status-${r.token}`}>{r.status}</span></td>
      <td className="py-3 pr-3"><div className="font-medium">{r.patient_name}</div><div className="text-xs text-ink-muted">{r.email || "no email"}</div></td>
      <td className="py-3 pr-3 text-xs">{r.lines.map((l) => l.label).join(", ")}</td>
      <td className="py-3 pr-3 font-medium">£{(r.total / 100).toFixed(2)}</td>
      <td className="py-3 pr-3 text-xs text-ink-muted">{when(r.created_at)}{r.paid_at ? ` · paid ${when(r.paid_at)}` : ""}{r.receipt_no && <div data-testid={`payreq-receipt-${r.token}`}>Receipt {r.receipt_no}</div>}</td>
      <td className="py-3 text-right whitespace-nowrap space-x-3">
        {r.status === "unpaid" && <>
          <button type="button" className="underline" onClick={() => onShow(r)} data-testid={`payreq-show-${r.token}`}>QR / open</button>
          {r.email && <button type="button" className="underline" onClick={() => act("send", "Link emailed.")} data-testid={`payreq-send-${r.token}`}>{r.emailed_at ? "Resend" : "Email"}</button>}
          <button type="button" className="underline text-red-700" onClick={() => window.confirm(`Cancel the payment link for ${r.patient_name}? It will stop working.`) && act("cancel", "Request cancelled.")} data-testid={`payreq-cancel-${r.token}`}>Cancel</button>
        </>}
        {r.status === "paid" && <ReceiptActions kind="payment-request" id={r.token} />}
      </td>
    </tr>
  );
};

const gbp = (p) => `£${(p / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Totals = ({ t }) => (
  <div className="grid grid-cols-2 gap-4 max-w-xl" data-testid="payreq-totals">
    {[["Paid this month", t.month_pence, t.month_count, "month"], ["Paid all time", t.all_pence, t.all_count, "all"]].map(([label, p, n, k]) => (
      <div key={k} className="bg-white border border-stone-200 rounded-md p-4">
        <div className="text-xs uppercase tracking-wider text-ink-muted">{label}</div>
        <div className="font-serif text-3xl mt-1" data-testid={`payreq-total-${k}`}>{gbp(p)}</div>
        <div className="text-xs text-ink-muted" data-testid={`payreq-count-${k}`}>{n} payment{n === 1 ? "" : "s"}</div>
      </div>
    ))}
  </div>
);

export default function AdminPayments() {
  const [rows, setRows] = useState(null);
  const [totals, setTotals] = useState(null);
  const [show, setShow] = useState(null);
  const load = useCallback(() => { api.get("/admin/payment-requests").then(({ data }) => { setRows(data.requests); setTotals(data.totals); }); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="container-ccc py-12 space-y-6" data-testid="admin-payments-page">
      <h1 className="font-serif text-4xl">Take a payment</h1>
      <AdminTabs active="/admin/payments" />
      <ResendBanner onDone={load} />
      {totals && <Totals t={totals} />}
      <StaffTestBooking onCreated={load} />
      <LinkStatusBoard refreshKey={rows} />
      {show && <LinkPanel req={show} onClose={() => setShow(null)} />}
      <PaymentRequestForm onCreated={(r) => { setShow(r); load(); }} />
      <div className="bg-white border border-stone-200 rounded-md p-6 overflow-x-auto">
        <div className="flex items-center"><h2 className="font-serif text-2xl">Payment requests</h2><button type="button" onClick={load} className="ml-auto text-sm underline" data-testid="payreq-refresh">Refresh</button></div>
        <table className="mt-3 w-full text-left" data-testid="payreq-table"><tbody>
          {rows?.map((r) => <Row key={r.token} r={r} onShow={setShow} onChanged={load} />)}
        </tbody></table>
        {rows && !rows.length && <p className="text-sm text-ink-muted mt-2">No payment requests yet.</p>}
      </div>
    </div>
  );
}
